/* zgl-landing.js — behaviour for the four zager-landing templates
   (page.zg-hub / zg-guide / zg-pillar / zg-lesson).

   Ported from the design's assets/js/site.js. Loaded (deferred) only by
   sections/zgl-site-header.liquid, so it never runs on any other template.

   Containment rules, so it cannot interfere with the rest of the theme:
     - one IIFE, nothing written to window;
     - every query runs inside a .zgl section wrapper, never document-wide;
     - the design's <html class="has-js"> becomes .zgl-has-js on each .zgl
       wrapper, which is what zgl-landing.css keys the scroll reveal off;
     - no cart, product form, Klaviyo or gift logic is touched.

   Theme editor: sections re-render in place, so each wrapper is initialised
   on shopify:section:load as well as on page load. */
(function () {
  'use strict';

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('zgl-is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    return revealObserver;
  }

  /* Scroll reveal — fades/rises .zgl-reveal elements in once. Without
     IntersectionObserver everything is shown rather than risk hidden copy. */
  function initReveal(root) {
    var observer = getRevealObserver();
    root.querySelectorAll('.zgl-reveal').forEach(function (el) {
      if (observer) observer.observe(el);
      else el.classList.add('zgl-is-visible');
    });
  }

  /* Hero image tilt — mouse only, skipped for reduced motion. The CSS that
     consumes these properties is gated on the same conditions. */
  function initTilt(root) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var MAX_TILT = 7;

    root.querySelectorAll('[data-zgl-hero-tilt]').forEach(function (media) {
      if (media.dataset.zglBound) return;
      media.dataset.zglBound = '1';

      media.addEventListener('pointermove', function (e) {
        var box = media.getBoundingClientRect();
        var x = (e.clientX - box.left) / box.width - 0.5;
        var y = (e.clientY - box.top) / box.height - 0.5;
        media.style.setProperty('--tilt-y', (x * MAX_TILT).toFixed(2) + 'deg');
        media.style.setProperty('--tilt-x', (-y * MAX_TILT).toFixed(2) + 'deg');
        media.style.setProperty('--shine-x', ((x + 0.5) * 100).toFixed(1) + '%');
        media.style.setProperty('--shine-y', ((y + 0.5) * 100).toFixed(1) + '%');
      });

      media.addEventListener('pointerleave', function () {
        media.style.setProperty('--tilt-x', '0deg');
        media.style.setProperty('--tilt-y', '0deg');
      });
    });
  }

  /* String demo — Karplus-Strong synthesis, no audio files. The first press
     creates the AudioContext (browsers forbid sound before interaction). */
  function initStringDemo(root) {
    var AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    root.querySelectorAll('[data-zgl-string-demo]').forEach(function (demo) {
      if (demo.dataset.zglBound) return;
      demo.dataset.zglBound = '1';

      var ctx = null;
      var master = null;
      var buffers = {};
      var unlocked = false;
      var dragging = false;
      var lastString = null;

      function start() {
        if (!ctx) {
          ctx = new AudioCtx();
          var limiter = ctx.createDynamicsCompressor();
          master = ctx.createGain();
          master.gain.value = 0.8;
          master.connect(limiter).connect(ctx.destination);
        }
        if (ctx.state === 'suspended') ctx.resume();
        unlocked = true;
      }

      function render(freq) {
        var rate = ctx.sampleRate;
        var length = Math.floor(rate * 2.6);
        var buffer = ctx.createBuffer(1, length, rate);
        var out = buffer.getChannelData(0);
        var period = Math.max(2, Math.round(rate / freq));
        var ring = new Float32Array(period);
        var i;

        for (i = 0; i < period; i++) ring[i] = (Math.random() * 2 - 1) * 0.6;

        for (i = 0; i < length; i++) {
          var read = i % period;
          var value = ring[read];
          out[i] = value;
          ring[read] = ((value + ring[(read + 1) % period]) / 2) * 0.996;
        }
        return buffer;
      }

      function pluck(button) {
        if (!unlocked) return;
        var freq = parseFloat(button.getAttribute('data-freq'));
        if (!freq) return;

        if (!buffers[freq]) buffers[freq] = render(freq);

        var source = ctx.createBufferSource();
        var gain = ctx.createGain();
        source.buffer = buffers[freq];
        gain.gain.setValueAtTime(0.35, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 2.5);
        source.connect(gain).connect(master);
        source.start();

        button.classList.remove('zgl-is-plucked');
        void button.offsetWidth;
        button.classList.add('zgl-is-plucked');
        window.clearTimeout(button.zglShiverTimer);
        button.zglShiverTimer = window.setTimeout(function () {
          button.classList.remove('zgl-is-plucked');
        }, 700);
      }

      demo.addEventListener('pointerdown', function (e) {
        var button = e.target.closest('[data-freq]');
        if (!button || !demo.contains(button)) return;
        start();
        dragging = true;
        lastString = button;
        pluck(button);
      });

      demo.addEventListener('pointermove', function (e) {
        if (!unlocked) return;
        if (e.pointerType !== 'mouse' && !dragging) return;

        var under = document.elementFromPoint(e.clientX, e.clientY);
        var button = under && under.closest ? under.closest('[data-freq]') : null;
        if (!button || !demo.contains(button)) { lastString = null; return; }
        if (button === lastString) return;
        lastString = button;
        pluck(button);
      });

      function release() { dragging = false; lastString = null; }
      demo.addEventListener('pointerup', release);
      demo.addEventListener('pointercancel', release);
      demo.addEventListener('pointerleave', release);

      /* Keyboard: Enter/Space fire click with detail 0. */
      demo.addEventListener('click', function (e) {
        if (e.detail !== 0) return;
        var button = e.target.closest('[data-freq]');
        if (!button || !demo.contains(button)) return;
        start();
        pluck(button);
      });
    });
  }

  /* Video facade — the poster is a real link to YouTube, swapped for the
     privacy-enhanced embed on click. Bound per facade, not on document. */
  function initVideo(root) {
    root.querySelectorAll('[data-zgl-video-facade]').forEach(function (trigger) {
      if (trigger.dataset.zglBound) return;
      trigger.dataset.zglBound = '1';

      trigger.addEventListener('click', function (e) {
        var id = trigger.getAttribute('data-video-id');
        if (!id) return;
        e.preventDefault();
        var frame = document.createElement('iframe');
        frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
        frame.title = trigger.getAttribute('data-video-title') || 'Video';
        frame.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture';
        frame.allowFullscreen = true;
        frame.setAttribute('loading', 'lazy');
        trigger.replaceWith(frame);
      });
    });
  }

  function init(root) {
    if (!root || !root.classList || !root.classList.contains('zgl')) return;
    root.classList.add('zgl-has-js');
    initReveal(root);
    initTilt(root);
    initStringDemo(root);
    initVideo(root);
  }

  function initAll() {
    document.querySelectorAll('.zgl').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor: a re-rendered section is a fresh wrapper. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
